import { z } from 'zod';

// Schema for policy parameters effect metadata
const parameterMetadataSchema = z.object({
  displayName: z.string(),
  description: z.string(),
});

// Schema for individual parameter
const parameterSchema = z.object({
  type: z.string(),
  metadata: parameterMetadataSchema,
  allowedValues: z.array(z.union([z.string(), z.boolean()])),
  defaultValue: z.any(),
});

// Schema for parameters: requires 'effect' and allows additional custom parameters
const parametersSchema = z.object({
  effect: parameterSchema,
}).catchall(parameterSchema);

// More flexible details schema that can handle various policy types
const detailsSchema = z.object({
  // Common fields
  type: z.string().optional(),
  name: z.string().optional(),
  resourceGroupName: z.string().optional(),
  subscriptionId: z.string().optional(),
  existenceScope: z.enum(['Subscription', 'ResourceGroup', 'subscription', 'resourceGroup']).optional(),
  deploymentScope: z.enum(['Subscription', 'ResourceGroup', 'subscription', 'resourceGroup']).optional(),
  evaluationDelay: z.string().optional(),
  existenceCondition: z.any().optional(),
  
  // Modify-specific fields
  roleDefinitionIds: z.array(z.string()).optional(),
  conflictEffect: z.string().optional(),
  operations: z.array(
    z.object({
      operation: z.string(),
      field: z.string(),
      value: z.union([z.string(), z.array(z.record(z.string(), z.any()))]),
    })
  ).optional(),
  
  // DINE-specific fields
  deployment: z.object({
    location: z.string().optional(),
    properties: z.object({
      mode: z.enum(['Incremental', 'Complete', 'incremental', 'complete']),
      template: z.record(z.any()),
      parameters: z.record(z.any()).optional(),
    }),
  }).passthrough().optional(),
}).passthrough();

// Schema for 'then' clause
const thenSchema = z.object({
  effect: z.string(),
  details: detailsSchema.optional(),
});

// Recursive schema for nested conditions (simplified to allow unknown structure)
const nestedConditionSchema: z.ZodTypeAny = z.lazy(() =>
  z.object({}).passthrough()
);

// Schema for policy rule
const policyRuleSchema = z.object({
  if: z.any(), // deferred validation of nested conditions
  then: thenSchema,
});

// Top-level schema for PolicyTemplate
export const policySchema = z.object({
  properties: z.object({
    displayName: z.string(),
    policyType: z.string(),
    mode: z.string(),
    description: z.string(),
    metadata: z.object({
      version: z.string(),
      category: z.string(),
      preview: z.boolean().optional(), // Make preview optional
    }).passthrough(), // Allow additional metadata properties
    version: z.string(),
    parameters: parametersSchema,
    policyRule: policyRuleSchema,
  }).passthrough(), // Allow additional properties at the root level
}).passthrough(); // Allow additional top-level properties

export type PolicySchema = z.infer<typeof policySchema>;